import { useContext, useEffect, useState } from "react";
import { useField } from "formik";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import { FiEye, FiEyeOff } from "react-icons/fi";
import PhoneInput from "react-phone-input-2";
import { PortalDateContext, PortalDateInput } from "components/PortalDatePicker";
import InputError from "components/InputError";
import FileField from "components/FileField";
import "./formFields.css";

// One field frame for native, PrimeReact and phone controls. Existing onChange
// overrides continue to receive the event shape used by their original control.
export default function FormField({ type = "text", name, label, className = "", inputClasses, width,
	filter = true, switchLabelPlacement = "right", isLoading = false, classNames,
	helperText, info, maxSize, fileHint, error: externalError, errorText, _onChange, ...rest }) {
	const [field, meta, helpers] = useField(name);
	const portalDates = useContext(PortalDateContext);
	const [showPassword, setShowPassword] = useState(false);
	const id = rest.id || name;
	const error = externalError ? errorText : meta.touched && meta.error;
	const help = helperText || info;
	const describedBy = [help && `${id}-help`, error && `${id}-error`, rest["aria-describedby"]].filter(Boolean).join(" ") || undefined;
	const controlClasses = `akello-field-control akello-control ${width || "w-full"} ${inputClasses || classNames?.input || ""}${error ? " p-invalid" : ""}`;
	const inputProps = { ...field, ...rest, id, "aria-invalid": !!error, "aria-describedby": describedBy };
	const fieldLabel = label || (type === "phone" ? "Mobile number" : undefined);

	// PrimeReact 6 does not forward these attributes to its keyboard helper inputs.
	useEffect(() => {
		const input = document.getElementById(id);
		if (!input) return;
		input.setAttribute("aria-invalid", String(!!error));
		if (describedBy) input.setAttribute("aria-describedby", describedBy);
		else input.removeAttribute("aria-describedby");
	}, [id, error, describedBy]);

	let control;
	if (type === "file") {
		control = <FileField id={id} name={name} label={fieldLabel} file={field.value} accept={rest.accept} maxSize={maxSize} hint={fileHint}
			disabled={rest.disabled} required={rest.required} invalid={!!error} describedBy={describedBy}
			onSelect={(file) => {
				if (rest.onChange) rest.onChange({ target: { name, id, files: file ? [file] : [], value: "" } });
				else helpers.setValue(file);
				helpers.setTouched(true, false);
			}} />;
	} else if (type === "phone") {
		control = <PhoneInput country="zw" countryCodeEditable={false} value={rest.value ?? field.value ?? ""}
			disabled={rest.disabled} placeholder={rest.placeholder || "e.g. +263 77 123 4567"}
			onChange={_onChange || ((value) => helpers.setValue(value ? `+${value}` : ""))}
			onBlur={(event) => { helpers.setTouched(true); rest.onBlur?.(event); }}
			inputProps={{ id, name, required: rest.required, "aria-invalid": !!error, "aria-describedby": describedBy }}
			containerClass="akello-phone-field" inputClass={controlClasses} />;
	} else if (type === "password") {
		control = <div className="akello-password-field">
			<InputText {...inputProps} type={showPassword ? "text" : "password"} className={controlClasses} />
			<button type="button" disabled={rest.disabled} aria-label={showPassword ? "Hide password" : "Show password"} aria-pressed={showPassword}
				onClick={() => setShowPassword((value) => !value)}>{showPassword ? <FiEyeOff aria-hidden="true" /> : <FiEye aria-hidden="true" />}</button>
		</div>;
	} else if (type === "date" && portalDates) {
		control = <PortalDateInput {...inputProps} label={fieldLabel} className="akello-date-field" />;
	} else if (type === "select" || type === "multiselect") {
		const Select = type === "multiselect" ? MultiSelect : Dropdown;
		control = <Select optionLabel="label" optionValue="value" filter={filter} showClear={Array.isArray(field.value) ? field.value.length > 0 : field.value !== "" && field.value != null}
			emptyMessage={isLoading ? "Loading options…" : "No options available"} emptyFilterMessage="No matching options"
			placeholder={isLoading ? "Loading options…" : "Select an option"}
			display={type === "multiselect" ? "chip" : undefined} {...field} {...rest}
			id={`${id}-wrapper`} inputId={id} ariaLabelledBy={fieldLabel ? `${id}-label` : undefined}
			className={controlClasses} panelClassName={`akello-field-options ${rest.panelClassName || ""}`} />;
	} else if (type === "switcher") {
		control = <div className={`akello-switch-field${switchLabelPlacement === "left" ? " label-first" : ""}`}>
			<InputSwitch {...field} {...rest} inputId={id} checked={field.value === true} />
			{fieldLabel && <label htmlFor={id} className={classNames?.label}>{fieldLabel}</label>}
		</div>;
	} else {
		const Input = type === "textarea" ? InputTextarea : InputText;
		control = <Input {...(type === "textarea" ? { rows: 5 } : { type })} {...inputProps} className={controlClasses} />;
	}
	return <div className={`akello-field${error ? " has-error" : ""} ${className}`}>
		{fieldLabel && type !== "switcher" && <label id={`${id}-label`} htmlFor={id} className={`akello-field-label ${classNames?.label || ""}`}>{fieldLabel}</label>}
		{control}
		{help && <div id={`${id}-help`} className="akello-field-help">{help}</div>}
		{error && <InputError id={`${id}-error`} error={error} />}
	</div>;
}
